import { useEffect } from "react";

type JsonLd = Record<string, unknown>;

/**
 * Lightweight, dependency-free SEO metadata for SPA routes. Sets the document
 * title, the <meta name="description"> content, an optional canonical link, and
 * an optional JSON-LD block while the component is mounted, then restores the
 * previous values on unmount. Google renders client JS, so this lets dynamic
 * routes (e.g. a product page) expose an accurate title/description and
 * structured data even though the app is a client-rendered SPA.
 *
 * No-ops for any field left undefined, so it's safe to call before data loads.
 */
export function useSeoMeta(opts: {
  title?: string;
  description?: string;
  canonical?: string;
  jsonLd?: JsonLd | null;
}): void {
  const { title, description, canonical, jsonLd } = opts;
  // Serialize jsonLd so the effect re-runs when its contents change.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    let descEl: HTMLMetaElement | null = null;
    let prevDesc: string | null = null;
    if (description) {
      descEl = document.querySelector('meta[name="description"]');
      if (descEl) {
        prevDesc = descEl.getAttribute("content");
        descEl.setAttribute("content", description);
      }
    }

    let canonEl: HTMLLinkElement | null = null;
    let createdCanon = false;
    let prevCanon: string | null = null;
    if (canonical) {
      canonEl = document.querySelector('link[rel="canonical"]');
      if (!canonEl) {
        canonEl = document.createElement("link");
        canonEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonEl);
        createdCanon = true;
      } else {
        prevCanon = canonEl.getAttribute("href");
      }
      canonEl.setAttribute("href", canonical);
    }

    let scriptEl: HTMLScriptElement | null = null;
    if (jsonLdKey) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-seo", "dynamic");
      scriptEl.text = jsonLdKey;
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute("content", prevDesc);
      if (canonEl) {
        if (createdCanon) canonEl.remove();
        else if (prevCanon !== null) canonEl.setAttribute("href", prevCanon);
      }
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, canonical, jsonLdKey]);
}
